<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import type { Page } from '~/jet/models';
    import PaddleIcon from '~assets/icons/paddle-default.svg';
    import ChevronIcon from '@amp/web-app-components/assets/icons/chevron-left-fill.svg';
    import ListPageItem from '~/components/ListPageItem.svelte';
    import {
        AMOUNT_OF_ITEMS_TO_FETCH,
        BUFFER_HEIGHT,
        HEIGHT_OF_EACH_ITEM,
    } from '~/constants/pagination';

    // Shared Components
    import VirtualScrollList from '@amp/web-app-components/src/components/VirtualScrollList/VirtualScrollList.svelte';
    import Header from '@amp/web-app-components/src/components/Header/Header.svelte';
    import DefaultPage from './DefaultPage.svelte';
    import AccessoryButton from '~/components/jet/buttons/AccessoryButton/AccessoryButton.svelte';

    // Shared Utilities
    import { loggerFor } from '@amp/web-apps-logger';
    import { getJet } from '~/jet';
    import { makePaginationIntent } from '~/jet/intents';

    // Store
    import { mediaQueries } from '@amp/web-app-components/src/stores/media-query';
    import { listScreenStore } from '~/stores/list-screen';
    import { showFooter } from '~/stores/show-footer';
    import { scrollIsLocked } from '@amp/web-app-components/src/stores/scroll-is-locked';
    import { getRafQueue } from '@amp/web-app-components/src/utils/rafQueue';
    import { openDrawer } from '~/stores/open-drawer';
    import { setLibraryStates } from '~/stores/local-library-state';

    export let page: Page;

    // In the event the API does not give us a total count of expected items,
    // provide a default to allow some pagination to happen.
    const DEFAULT_ITEM_COUNT = 350;

    const listViewTypes = ['artist', 'work', 'album', 'recording'];

    const jet = getJet();
    const log = loggerFor('<ListPage>');
    const rafQueue = getRafQueue();

    let showListView = true;
    let selectedItemIndex = null;

    $: header = page.header;
    $: listPageSection = page.sections?.[0];
    $: items = listPageSection?.items;

    // Basing the amount of items per page to be based on the first
    // page item total, with a default value of AMOUNT_OF_ITEMS_TO_FETCH
    // to get around the potential for different endpoints returning varied
    // number of items in their paginated reponses.
    $: amountToFetch = !!items.length ? items.length : AMOUNT_OF_ITEMS_TO_FETCH;
    $: itemCount = listPageSection?.pageMetadata?.total || DEFAULT_ITEM_COUNT;
    $: isMobile = $mediaQueries === 'xsmall' || $mediaQueries === 'small';
    $: {
        if (page) {
            selectedItemIndex = 0;
        }
    }

    $: {
        if (!isMobile) {
            jet?.perform?.(items?.[selectedItemIndex]?.clickAction);
        }
    }

    $: {
        if (header?.entities?.length) {
            setLibraryStates(page.header.entities);
        }
    }

    // Matches specific index of listViewTypes with related itemKind to
    // determine if List Page contains the Artwork, Work, or Album Header Lockups
    // Indexes: [0] = 'artist', [1] = 'work', [2, 3] = 'album' & 'recording' - (corresponds w album lockup)
    $: hasArtworkHeader = listViewTypes[0].includes(listPageSection.itemKind);
    $: hasWorkHeader = listViewTypes[1].includes(listPageSection.itemKind);
    $: hasAlbumHeader = listViewTypes.includes(listPageSection.itemKind, 2);

    // Determine if Queue or Info Panel is expanded
    $: isDrawerOpen = !!$openDrawer;

    const handleSelect = async (e: CustomEvent) => {
        const { id } = e.detail;
        selectedItemIndex = items.findIndex((item) => item.id === id);
        if (isMobile) {
            await jet.perform(items?.[selectedItemIndex]?.clickAction);
            showListView = false;
        }
    };

    const handleBackButton = () => {
        showListView = true;
        listScreenStore.resetStore();
    };

    function handleUpdateItemsCount(e: CustomEvent): void {
        rafQueue.add(() => (itemCount = e.detail));
    }

    onMount(() => {
        showFooter.set(false);
        scrollIsLocked.set(true);

        return () => {
            listScreenStore.resetStore();
            showFooter.set(true);
        };
    });

    onDestroy(() => {
        scrollIsLocked.set(false);
    });
</script>

<div class="list-page-container" data-testid="list-page">
    <div
        class="list-page-items-list"
        class:hide={!showListView}
        data-testid="list-container"
    >
        <Header title={header?.title}>
            <svelte:fragment slot="buttons">
                {#if header?.sorting}
                    <AccessoryButton button={header.sorting} />
                {/if}
            </svelte:fragment>
        </Header>
        <VirtualScrollList
            items={listPageSection?.items ?? []}
            {itemCount}
            fetchIntentFn={makePaginationIntent}
            bufferHeight={BUFFER_HEIGHT}
            {amountToFetch}
            itemHeight={HEIGHT_OF_EACH_ITEM}
            bottomSpacerHidden={true}
            restrainContainerWidth={true}
            rowZebraStriping={false}
            selectedItemId={items[selectedItemIndex]?.id}
            on:select={handleSelect}
            {log}
            {jet}
            useManualPagination={false}
            pageMetadata={listPageSection?.pageMetadata}
            allowCountUpdates={!listPageSection?.pageMetadata?.total}
            on:updateItemsCount={handleUpdateItemsCount}
        >
            <div slot="itemComponent" let:isSelected let:item let:handleClick>
                <ListPageItem
                    {item}
                    {isSelected}
                    on:itemClicked={handleClick}
                />
            </div>
        </VirtualScrollList>
    </div>
    <article
        class="list-page-details-view"
        class:hide={showListView}
        class:artist-lockup-panel-open={hasArtworkHeader && isDrawerOpen}
        class:work-lockup-panel-open={hasWorkHeader && isDrawerOpen}
        class:album-lockup-panel-open={hasAlbumHeader && isDrawerOpen}
        data-testid="details-container"
    >
        {#if $listScreenStore.detailPage}
            {#if isMobile}
                <div
                    class="back-button-container"
                    data-testid="back-button-container"
                >
                    {#if hasArtworkHeader}
                        <button
                            type="button"
                            class="back-button back-button--material"
                            aria-label={header.title}
                            data-testid="back-button-artwork-header"
                            on:click={handleBackButton}
                        >
                            <ChevronIcon class="icon" aria-hidden="true" />
                        </button>
                    {:else}
                        <button
                            type="button"
                            class="back-button back-button--text"
                            data-testid="back-button"
                            on:click={handleBackButton}
                        >
                            <PaddleIcon class="icon" aria-hidden="true" />
                            <span>{header.title}</span>
                        </button>
                    {/if}
                </div>
            {/if}
            <DefaultPage page={$listScreenStore.detailPage} />
        {/if}
    </article>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/margin-gutter' as *;
    @use 'ac-sasskit/core/locale' as *;
    @use 'amp/stylekit/core/mixins/materials' as *;

    $list-spacing-small: 12px;
    $list-header-height: 52px;
    $list-detail-sidebar-width: 300px;
    $two-column-sidebar-width: calc(
        $global-sidebar-width-large + $list-detail-sidebar-width
    );

    // match height of cloud buttons grid area
    $cloud-button-grid-size: 28px;

    .list-page-container {
        background-color: initial;
        height: calc(100dvh - $global-player-bar-height);

        @media (--small) {
            --sidebar-width-expanded: #{$two-column-sidebar-width};
            display: grid;
            grid-template-columns: $list-detail-sidebar-width 1fr;
        }

        :global(.title) {
            font: var(--title-2-emphasized-serif);
        }

        :global(.container.scrollable-container-constrained-width) {
            height: calc(
                100dvh - ($global-player-bar-height + $list-header-height)
            );

            @media (--small) {
                width: 100%;
            }
        }

        :global(.virtual-row) {
            padding-inline-start: $list-spacing-small;
            padding-inline-end: $list-spacing-small;
        }

        // Include keyline border per list item, excluding the currently selected item
        :global(.virtual-row + .virtual-row .list-page-item:not(.is-selected)) {
            border-top: var(--keyline-border-style);
        }

        // Visually hide keyline border of items that follow the selected item
        :global(.virtual-row:has(.is-selected) + .virtual-row .list-page-item) {
            --keyline-border-style: 0.5px solid transparent;
        }
    }

    .list-page-items-list {
        :global(.header) {
            align-items: center;
            margin-inline-start: $list-spacing-small;
            margin-inline-end: $list-spacing-small;
            margin-top: 14px;
            margin-bottom: $list-spacing-small;
        }
    }

    // On small screens, toggle the visibility of list page content
    // when switching between list view and details view
    .hide {
        @media (--range-xsmall-down) {
            display: none;
        }
    }

    .list-page-details-view {
        overflow-x: hidden;
        overflow-y: auto;

        // Apply material button styles when back button appears over
        // full width artwork headers
        &:global(:has(.artist-header--full-width) .back-button) {
            --backButtonIconFill: white;

            @include system-standard-thin-material(dark);
        }

        // Prevent mobile mini player from overlapping List Page content
        :global(.section:last-of-type) {
            @media (--range-sidebar-hidden-down) {
                padding-bottom: 40px;
            }
        }

        @media (--range-xsmall-down) {
            position: relative;
            // set height on small screens to allow scrolling
            height: 100%;
        }
    }

    // Prevent errant layout shifts on library pages with ARTIST header lockups
    // when the queue / info panel is open at breakpoints above 1259px
    .artist-lockup-panel-open {
        @media (--medium) {
            // Increase height of artist header lockup to prevent long artist names from
            // overlapping ellipsis artwork when full width artwork is unavailable
            :global(
                    .artist-header:not(
                            .artist-header--full-width,
                            .artist-header--no-artwork
                        )
                ) {
                $artist-header-height-tall: 410px;
                height: $artist-header-height-tall;
            }

            // Prevent artist details from getting clipped when panel is open
            // and no artwork is available
            :global(.artist-header--no-artwork .artist-header__name-container) {
                position: relative;
            }
        }
    }

    // On library pages with WORK header lockups, align cloud buttons to top right
    // to prevent overlap with work description when the queue / info panel is open
    // at breakpoints above 1259px
    .work-lockup-panel-open {
        @media (--range-medium-only) {
            :global(.work-header-container) {
                padding-top: 20px;
                grid-template-areas:
                    '. actions'
                    'composer composer'
                    'title title'
                    'subtitle subtitle'
                    'description description';
            }
        }
    }

    // On library pages with ALBUM header lockups, allow album lockup to
    // flow into a single column between 1260px and 1589px
    // when the queue / info panel is open
    .album-lockup-panel-open {
        @media (--range-medium-only) {
            :global(.container-detail-header) {
                grid-template-areas:
                    'secondary-actions'
                    'artwork'
                    'headings'
                    'primary-actions'
                    'description';
                justify-items: center;
                grid-template-columns: auto;
                grid-template-rows: auto;
            }

            :global(.artwork) {
                margin-top: 15px;
                margin-inline: auto;
            }

            :global(.headings) {
                margin-top: 18px;
                align-items: center;
            }

            :global(.headings__title),
            :global(.headings__subtitles) {
                text-align: center;
            }

            :global(.secondary-actions) {
                margin-inline-end: 0;
            }
        }
    }

    .back-button-container {
        @media (--range-xsmall-down) {
            position: absolute;
            top: 0;
            inset-inline-start: 0;
            z-index: calc(var(--z-default) + 2);
        }
    }

    .back-button {
        color: var(--keyColor);
        display: flex;
        align-items: center;
        height: $cloud-button-grid-size;

        @include margin-gutter(20px);

        @media (--small) {
            display: none;
        }

        :global(.icon) {
            @include rtl {
                transform: scaleX(-1);
            }
        }

        :global(.icon path) {
            fill: var(--backButtonIconFill, var(--keyColor));
        }
    }

    .back-button--text {
        position: relative;

        :global(.icon) {
            height: 14px;
            width: auto;
            position: absolute;
            inset-inline-start: -12px;
        }
    }

    .back-button--material {
        width: $cloud-button-grid-size;
        justify-content: center;
        border-radius: 50%;
        background-color: var(--systemQuinary);

        :global(.icon) {
            height: inherit;
            width: inherit;
        }

        :global(.icon circle) {
            fill: transparent;
        }
    }
</style>
